import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import { t } from 'ember-intl';
import SvgLogo from 'madow-tech/components/svg-logo';
import Icon from 'madow-tech/components/icon';
import SpaceButton from 'madow-tech/components/space-button';
import RocketButton from 'madow-tech/components/rocket-button';
import scrollNavbar from 'madow-tech/modifiers/scroll-navbar';
import BasicDropdown from 'ember-basic-dropdown/components/basic-dropdown';

export default class Navbar extends Component {
  @service('intl') intl: any;
  @service('router') router!: RouterService;

  get routerService(): RouterService | null {
    try {
      if (this.router) {
        return this.router;
      }
      // Fallback: try to get router from owner
      const owner = getOwner(this);
      return owner?.lookup('service:router') as RouterService | null;
    } catch {
      return null;
    }
  }
  @tracked showMobileNavbar = false;
  @tracked isMobileMenuOpen = false;
  @tracked localeKey: string = 'en';

  constructor(owner: unknown, args: any) {
    super(owner, args);
    this.updateLocale();
  }

  updateLocale() {
    const locales = this.intl.locale;
    this.localeKey = Array.isArray(locales) ? locales[0] || 'en' : locales || 'en';
  }

  get currentLocale(): string {
    // Use URL as source of truth for reliable locale detection
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (path.startsWith('/es')) return 'es';
      if (path.startsWith('/en')) return 'en';
    }
    // Fallback to intl service if URL doesn't provide locale
    const locales = this.intl.locale;
    return Array.isArray(locales) ? locales[0] || 'en' : locales || 'en';
  }

  get isEnglish(): boolean {
    // Also check URL path as fallback for SSR/hydration
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (path.startsWith('/es')) return false;
      if (path.startsWith('/en')) return true;
    }
    return this.currentLocale === 'en';
  }

  get localeDisplayText(): string {
    return this.isEnglish ? 'EN' : 'ES';
  }

  /**
   * Get the correct URL for a section link.
   * If we're on the home page, use hash anchor.
   * If we're on another page (like contact-success), navigate to home page with hash.
   */
  getSectionUrl(section: string): string {
    // Safety check: if router is not available yet, default to hash anchor
    try {
      const router = this.routerService;
      if (!router || !router.currentRouteName) {
        return `#${section}`;
      }

      const currentRouteName = router.currentRouteName;
      const isOnHomePage =
        currentRouteName === 'en.index' ||
        currentRouteName === 'es.index' ||
        currentRouteName === 'index' ||
        currentRouteName === 'en' ||
        currentRouteName === 'es';

      if (isOnHomePage) {
        // On home page, use hash anchor
        return `#${section}`;
      } else {
        // Not on home page, navigate to home page with hash
        const locale = this.currentLocale;
        return `/${locale}#${section}`;
      }
    } catch (e) {
      // If anything goes wrong, default to hash anchor
      return `#${section}`;
    }
  }

  @action
  handleSectionClick(section: string, event: MouseEvent) {
    // Safety check: if router is not available, let default hash navigation work
    const router = this.routerService;
    if (!router || !router.currentRouteName) {
      return;
    }

    const currentRouteName = router.currentRouteName;
    const isOnHomePage =
      currentRouteName === 'en.index' ||
      currentRouteName === 'es.index' ||
      currentRouteName === 'index' ||
      currentRouteName === 'en' ||
      currentRouteName === 'es';

    if (!isOnHomePage) {
      // Prevent default hash navigation
      event.preventDefault();
      // Navigate to home page with hash
      const locale = this.currentLocale;
      router.transitionTo(`${locale}.index`).then(() => {
        // After navigation, scroll to section
        setTimeout(() => {
          const element = document.querySelector(`#${section}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      });
    }
    // If on home page, let default hash navigation work (don't prevent default)
  }

  @action
  handleServicesClick(event: MouseEvent) {
    this.handleSectionClick('services', event);
  }

  @action
  handleSuccessClick(event: MouseEvent) {
    this.handleSectionClick('success', event);
  }

  @action
  handleTechnologiesClick(event: MouseEvent) {
    this.handleSectionClick('technologies', event);
  }

  @action
  handleTeamClick(event: MouseEvent) {
    this.handleSectionClick('team', event);
  }

  @action
  handleContactClick(event: MouseEvent) {
    this.handleSectionClick('contact', event);
  }

  @action
  handleTopClick(event: MouseEvent) {
    this.handleSectionClick('top', event);
  }

  @action
  updateNavbarVisibility(isVisible) {
    this.showMobileNavbar = isVisible;
  }

  @action
  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  @action
  closeMobileMenu() {
    this.isMobileMenuOpen = false;
  }

  @action
  toggleLocale() {
    // Determine current locale from URL for reliable detection
    const currentPath = window.location.pathname;
    const currentIsEnglish = currentPath.startsWith('/en') || (!currentPath.startsWith('/es') && this.currentLocale === 'en');
    const newLocale = currentIsEnglish ? 'es' : 'en';

    // Preserve current path, just switch locale prefix
    let newPath = `/${newLocale}`;
    if (currentPath.startsWith('/en/')) {
      newPath = currentPath.replace('/en/', `/${newLocale}/`);
    } else if (currentPath.startsWith('/es/')) {
      newPath = currentPath.replace('/es/', `/${newLocale}/`);
    } else if (currentPath === '/en' || currentPath === '/es') {
      newPath = `/${newLocale}`;
    }

    // Navigate to the language URL for SEO
    window.location.href = newPath;
  }


  <template>
    <div {{scrollNavbar this.updateNavbarVisibility}} class="navbar-container md:sticky top-0 z-50">
      {{! Desktop navbar - always visible on sm+ }}
      <header aria-label="Desktop navigation" class="hidden sm:block z-40 backdrop-blur border-b border-mars-900/30 overflow-x-hidden">
        <div class="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-4">
          <a href={{this.getSectionUrl "top"}} {{on "click" this.handleTopClick}} class="flex items-center gap-3 group">
            <SvgLogo class="w-auto sm:h-6 relative -top-0.5" @imgClass="h-16" />
            <span class="sr-only">Madow Tech</span>
          </a>
          <nav aria-label="Desktop menu links" class="ml-auto hidden md:flex gap-6 text-sm text-zinc-300 items-center">
            <a href={{this.getSectionUrl "services"}} {{on "click" this.handleServicesClick}} class="hover:text-white transition-colors">{{t "navbar.services"}}</a>
            <a href={{this.getSectionUrl "success"}} {{on "click" this.handleSuccessClick}} class="hover:text-white transition-colors">{{t "navbar.success"}}</a>
            <a href={{this.getSectionUrl "technologies"}} {{on "click" this.handleTechnologiesClick}} class="hover:text-white transition-colors">{{t "navbar.technologies"}}</a>
            <a href={{this.getSectionUrl "team"}} {{on "click" this.handleTeamClick}} class="hover:text-white transition-colors">{{t "navbar.team"}}</a>
            <a href={{this.getSectionUrl "contact"}} {{on "click" this.handleContactClick}} class="hover:text-white transition-colors">{{t "navbar.contact"}}</a>

            <div class="flex items-center">
              <span class="text-zinc-500 mr-4">|</span>

              <BasicDropdown as |dd|>
                <dd.Trigger class="hover:text-white transition-colors cursor-pointer flex items-center gap-1 text-sm text-zinc-300">
                  <span>{{t "navbar.more"}}</span>
                  <svg class="h-3 w-3 transition-transform {{if dd.isOpen 'rotate-180'}}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </dd.Trigger>
                <dd.Content class="dropdown-content bg-black/90 backdrop-blur-xl border border-white/10 rounded-2xl mt-4 py-2 min-w-[160px] shadow-lg">
                  <a href="/{{this.currentLocale}}/services/ember" class="block px-4 py-2.5 hover:bg-white/10 transition-colors text-sm text-zinc-300 hover:text-white">
                    Ember.js
                  </a>
                </dd.Content>
              </BasicDropdown>
            </div>
          </nav>
          <div class="ml-auto flex items-center gap-4">
            <RocketButton
              @text={{t "navbar.book_a_call"}}
              class="!p-3 !hidden sm:!block"
            />
            {{! Language Selector - Rightmost position }}
            <button
              type="button"
              {{on "click" this.toggleLocale}}
              class="flex items-center gap-2.5 px-5 py-2.5 rounded-xl hover:bg-white/10 transition-all border border-white/20 hover:border-white/30"
              title="{{if this.isEnglish (t 'navbar.change_to_spanish') (t 'navbar.change_to_english')}}"
              aria-label="{{if this.isEnglish (t 'navbar.change_to_spanish') (t 'navbar.change_to_english')}}"
            >
              <Icon @name="svg-globe" @svgClass="h-5 w-5" />
              <span class="text-base font-medium">{{this.localeDisplayText}}</span>
            </button>
          </div>
        </div>
      </header>

      {{! Mobile hamburger - always visible on mobile at top }}
      <div class="mobile-hamburger-wrapper fixed sm:hidden top-0 right-0 z-50 p-3 {{if this.showMobileNavbar 'is-hidden'}}">
        <button
          type="button"
          {{on "click" this.toggleMobileMenu}}
          class="hamburger-btn relative w-10 h-10 flex items-center justify-center rounded-lg bg-black/50 backdrop-blur-sm hover:bg-white/10 transition-colors"
          aria-label="Toggle menu"
          aria-expanded={{if this.isMobileMenuOpen "true" "false"}}
        >
          <div class="hamburger-icon {{if this.isMobileMenuOpen 'is-open'}}">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </div>

      {{! Mobile navbar header - shows after scroll }}
      <header aria-label="Mobile navigation" class="mobile-navbar {{if this.showMobileNavbar 'is-visible'}} fixed md:hidden top-0 left-0 right-0 z-50">
        <div class="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-4">
          <a href={{this.getSectionUrl "top"}} {{on "click" this.handleTopClick}} class="flex items-center gap-3 group">
            <SvgLogo class="h-5 w-auto relative -top-0.5" @imgClass="h-16" />
            <span class="sr-only">Madow Tech</span>
          </a>

          {{! Hamburger Button }}
          <button
            type="button"
            {{on "click" this.toggleMobileMenu}}
            class="hamburger-btn relative w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Toggle menu"
            aria-expanded={{if this.isMobileMenuOpen "true" "false"}}
          >
            <div class="hamburger-icon {{if this.isMobileMenuOpen 'is-open'}}">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        </div>
      </header>

      {{! Mobile Menu Dropdown - shared between both hamburgers }}
      {{#if this.isMobileMenuOpen}}
        <nav aria-label="Mobile menu links" class="mobile-menu-dropdown fixed sm:hidden top-16 left-0 right-0 bottom-0 z-40 border-t border-white/10 backdrop-blur-xl bg-black/90 overflow-auto">
          <div class="mx-auto max-w-7xl px-4 py-4 flex flex-col gap-3">
            <a href={{this.getSectionUrl "services"}} {{on "click" this.handleServicesClick}} {{on "click" this.closeMobileMenu}} class="mobile-menu-link">
              <Icon @name="svg-wrench" @svgClass="h-4 w-4" />
              <span>{{t "navbar.services"}}</span>
            </a>
            <a href={{this.getSectionUrl "success"}} {{on "click" this.handleSuccessClick}} {{on "click" this.closeMobileMenu}} class="mobile-menu-link">
              <Icon @name="svg-star" @svgClass="h-4 w-4" />
              <span>{{t "navbar.success"}}</span>
            </a>
            <a href={{this.getSectionUrl "technologies"}} {{on "click" this.handleTechnologiesClick}} {{on "click" this.closeMobileMenu}} class="mobile-menu-link">
              <Icon @name="svg-cpu" @svgClass="h-4 w-4" />
              <span>{{t "navbar.technologies"}}</span>
            </a>
            <a href={{this.getSectionUrl "team"}} {{on "click" this.handleTeamClick}} {{on "click" this.closeMobileMenu}} class="mobile-menu-link">
              <Icon @name="svg-users" @svgClass="h-4 w-4" />
              <span>{{t "navbar.team"}}</span>
            </a>
            <a href={{this.getSectionUrl "contact"}} {{on "click" this.handleContactClick}} {{on "click" this.closeMobileMenu}} class="mobile-menu-link">
              <Icon @name="svg-rocket" @svgClass="h-4 w-4" />
              <span>{{t "navbar.contact"}}</span>
            </a>

            {{! More section - inline en mobile }}
            <div class="border-t border-white/10 my-2"></div>
            <span class="text-xs text-zinc-500 px-4 uppercase tracking-wider">{{t "navbar.more"}}</span>
            <a href="/{{this.currentLocale}}/services/ember" {{on "click" this.closeMobileMenu}} class="mobile-menu-link">
              <Icon @name="svg-rocket" @svgClass="h-4 w-4" />
              <span>Ember.js</span>
            </a>

            {{! Language Selector - Mobile }}
            <button
              type="button"
              {{on "click" this.toggleLocale}}
              class="mobile-menu-link justify-between"
              title="{{if this.isEnglish (t 'navbar.change_to_spanish') (t 'navbar.change_to_english')}}"
              aria-label="{{if this.isEnglish (t 'navbar.change_to_spanish') (t 'navbar.change_to_english')}}"
            >
              <div class="flex items-center gap-3">
                <Icon @name="svg-globe" @svgClass="h-5 w-5" />
                <span>{{t "navbar.language"}}</span>
              </div>
              <span class="text-sm px-3 py-1.5 rounded bg-white/10 font-medium">{{this.localeDisplayText}}</span>
            </button>

            <div class="pt-2 mt-2 border-t border-white/10">
              <RocketButton
                @text={{t "navbar.book_a_call"}}
                @onClick={{this.closeMobileMenu}}
                class="!w-full !justify-center"
              />
            </div>
          </div>
        </nav>
      {{/if}}
    </div>
  </template>
}
